<template>
    <div class="kt-portlet">
        <div class="kt-portlet__body kt-portlet__body--fluid row">
            <MainLayoutSelectBox
                v-model="selectBoxPeriod"
                :enumDataSource="DashboardStatsPeriod"
                :defaultValue="DashboardStatsPeriod.Today"
                enumTranslationNamespace="enums.DashboardStatsPeriod"
                :enumTranslationParams="periodTranslationParams"
                size="col-lg-3 col-md-6"
            />

            <DatePicker
                v-if="datePickerEnabled"
                inputId="select-date-input"
                v-model="selectedDate"
                :min-date="minDate"
                :max-date="maxDate"
                showIcon
                fluid
                iconDisplay="input"
                dateFormat="dd.mm.yy"
                size="col-lg-3 col-md-6"
            />

            <MainLayoutSelectBox
                v-if="isAdmin"
                v-model="selectBoxLocation"
                :dataSource="Factory.getInstance<LocationDao>(LocationDao)"
                :showAllText="i18n('general.locationSwitch.pleaseSelect')"
                :formatter="locationSelectBoxTextFormatter"
                size="col-lg-3 col-md-6"
            />
        </div>
    </div>

    <div class="kt-portlet" v-if="!paramsSelected">
        <div class="kt-portlet__head">
            <div class="kt-portlet__head-label">
                <h4 class="kt-portlet__head-title" style="font-weight: normal; font-size: 1.1rem">
                    {{ i18n("dashboard.selectLocation") }}
                </h4>
            </div>
        </div>
    </div>

    <template v-if="!originalLayout">
        <div class="row" v-if="paramsSelected">
            <div class="col-lg-4">
                <SalesChart :locationId="selectedLocation" :period="selectedPeriod" :selectedDate="selectedDate" />
            </div>
            <div class="col-lg-4">
                <SalesByPaymentMethodChart
                    :locationId="selectedLocation"
                    :period="selectedPeriod"
                    :selectedDate="selectedDate"
                />
            </div>
            <div class="col-lg-4">
                <CancellationsChart
                    :locationId="selectedLocation"
                    :period="selectedPeriod"
                    :selectedDate="selectedDate"
                />
            </div>
        </div>

        <div class="row" v-if="paramsSelected">
            <div class="col-lg-4"></div>
            <div class="col-lg-4">
                <KpiSummary :locationId="selectedLocation" :period="selectedPeriod" :selectedDate="selectedDate" />
            </div>
            <div class="col-lg-4"></div>
        </div>
    </template>

    <template v-if="originalLayout">
        <div class="row" v-if="paramsSelected">
            <div class="col-lg-4">
                <SalesChart :locationId="selectedLocation" :period="selectedPeriod" :selectedDate="selectedDate" />
            </div>
            <div class="col-lg-4">
            </div>
            <div class="col-lg-4">
                <SalesByPaymentMethodChart
                    :locationId="selectedLocation"
                    :period="selectedPeriod"
                    :selectedDate="selectedDate"
                />
            </div>
        </div>

        <div class="row" v-if="paramsSelected">
            <div class="col-lg-4">
                <KpiSummary :locationId="selectedLocation" :period="selectedPeriod" :selectedDate="selectedDate" />
            </div>
            <div class="col-lg-4"></div>
            <div class="col-lg-4">
                <CancellationsChart
                    :locationId="selectedLocation"
                    :period="selectedPeriod"
                    :selectedDate="selectedDate"
                />
            </div>
        </div>
    </template>

    <div class="row" v-if="isAdmin">
        <div class="col-lg-4">
            <AdminRecentActivities />
        </div>

        <div class="col-lg-8">
            <div class="row">
                <div class="col-lg-6">
                    <MonthlySignupChart />
                </div>
                <div class="col-lg-6">
                    <DailyAllocationChart />
                </div>
                <div class="col-lg-6">
                    <UsersOnline />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { type IOfflineStorage, OfflineStorage } from "@/data/dao/offline/OfflineStorage";
    import { LocationDao } from "@/data/dao/service/LocationDao";
    import { DashboardStatsPeriod } from "@/data/model/Enums";
    import type { ILocation } from "@/data/model/Location";
    import { i18n, isInRole } from "@/portal/Trans";
    import { portalEvents } from "@/portal/events/portalEvents";
    import type { IOptionNameFormatter } from "@/portal/formControls/Select";
    import { TastierGlobal } from "@/portal/globalAccess";
    import { LocationSwitch } from "@/portal/ui/RestaurantSwitch";
    import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from "vue";
    import DatePicker from "primevue/calendar";
    import { usePrimeVue } from "primevue/config";

    import CancellationsChart from "@/portal/components/dashboard/CancellationsChart.vue";
    import DailyAllocationChart from "@/portal/components/dashboard/DailyAllocationChart.vue";
    import MonthlySignupChart from "@/portal/components/dashboard/MonthlySignupChart.vue";
    import SalesByPaymentMethodChart from "@/portal/components/dashboard/SalesByPaymentMethodChart.vue";
    import SalesChart from "@/portal/components/dashboard/SalesChart.vue";
    import AdminRecentActivities from "@/portal/components/dashboard/widgets/AdminRecentActivities.vue";
    import KpiSummary from "@/portal/components/dashboard/widgets/KpiSummary.vue";
    import UsersOnline from "@/portal/components/dashboard/widgets/UsersOnline.vue";
    import MainLayoutSelectBox from "@/portal/components/elements/MainLayoutSelectBox.vue";

    // the portal never sets a PrimeVue locale, and PrimeVue's default calendar week starts on Sunday
    const primevue = usePrimeVue();
    if (primevue.config.locale) primevue.config.locale.firstDayOfWeek = 1;

    const originalLayout = ref(false);
    const isAdmin = ref(isInRole("Admin"));
    const selectBoxPeriod = ref<string | undefined>(undefined);
    const selectedDate = ref<Date | undefined>(new Date());
    const minDate = computed(() => {
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 12);
        startDate.setHours(0, 0, 0, 0);
        return startDate;
    });

    const maxDate = computed(() => {
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);
        return endOfDay;
    });
    const selectBoxLocation = ref<string | undefined>(undefined);
    const locationSwitchLocation = ref<string | undefined>(undefined);

    // the three years before last year are labelled with the year they stand for
    const periodTranslationParams = computed(() => {
        const currentYear = new Date().getFullYear();
        return {
            TwoYearsAgo: { year: currentYear - 2 },
            ThreeYearsAgo: { year: currentYear - 3 },
            FourYearsAgo: { year: currentYear - 4 },
        };
    });

    const datePickerEnabled = computed(() => {
        return selectBoxPeriod.value === DashboardStatsPeriod.Date.toString();
    });
    const selectedLocation = computed(() => (isAdmin.value ? selectBoxLocation.value : locationSwitchLocation.value));
    const selectedPeriod = computed(() =>
        selectBoxPeriod.value !== undefined ? (parseInt(selectBoxPeriod.value) as DashboardStatsPeriod) : undefined
    );
    const paramsSelected = computed(
        () =>
            selectedLocation.value !== undefined &&
            selectedPeriod.value !== undefined &&
            selectedDate.value !== undefined
    );

    const locationSelectBoxTextFormatter: IOptionNameFormatter<any> = (e: ILocation) => {
        return e.internalName == null || e.internalName.length === 0 ? e.name : e.internalName;
    };

    function init() {
        // for admins - preselect previously selected location
        if (isAdmin.value) {
            const offlineStorage = Factory.getInstance<IOfflineStorage>(OfflineStorage);
            const cachedSelectedLocation = offlineStorage.read(LocationSwitch.currentLocationCacheKey);
            if (cachedSelectedLocation?.id) {
                selectBoxLocation.value = cachedSelectedLocation.id;
            }
        }
        // other users have location switch
        else {
            refreshLocationSwitchLocation();
        }
    }

    function refreshLocationSwitchLocation() {
        locationSwitchLocation.value = TastierGlobal.getLocationSwitch()?.getCurrentLocationId();
    }

    onBeforeMount(() => {
        portalEvents.OnLocationChanged.add(refreshLocationSwitchLocation);
    });

    onBeforeUnmount(() => {
        portalEvents.OnLocationChanged.remove(refreshLocationSwitchLocation);
    });

    onMounted(init);
</script>
